/* <expandable-text>
 * Markup contract (set up by sections/rich-text.liquid):
 *   <expandable-text
 *     data-word-count="200"
 *     data-word-count-mobile="50"
 *     data-expand-label="Read more"
 *     data-collapse-label="Read less"
 *   >
 *     <div class="expandable-text__content">…full richtext…</div>
 *     <button class="expandable-text__button" hidden>Read more</button>
 *   </expandable-text>
 *
 * On connect we measure where the Nth word lands inside the content,
 * lock max-height to that offset, reveal the button, and toggle on
 * click. Word counting walks text nodes only, so HTML formatting in
 * the richtext is preserved when the content expands.
 */
class ExpandableText extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('.expandable-text__content');
    this.button = this.querySelector('.expandable-text__button');
    if (!this.content || !this.button) return;

    this.wordCountDesktop = parseInt(this.dataset.wordCount, 10) || 200;
    this.wordCountMobile = parseInt(this.dataset.wordCountMobile, 10) || this.wordCountDesktop;
    this.expandLabel = this.dataset.expandLabel || 'Read more';
    this.collapseLabel = this.dataset.collapseLabel || 'Read less';
    this.expanded = false;
    this.resizeTimer = null;
    this.measured = false;
    this.lastWidth = window.innerWidth;

    this.button.textContent = this.expandLabel;
    this.button.setAttribute('aria-expanded', 'false');
    this.button.setAttribute('aria-controls', this.content.id || '');
    this.button.addEventListener('click', () => this.toggle());

    // Once fully open, release the px clamp so late-loading images/fonts
    // can't clip the expanded content.
    this.content.addEventListener('transitionend', (event) => {
      if (event.propertyName === 'max-height' && this.expanded) {
        this.content.style.maxHeight = 'none';
      }
    });

    this.applyCollapsedHeight();
    window.addEventListener('resize', this.onResize.bind(this));
  }

  onResize() {
    // iOS fires resize on every scroll (URL bar collapse/expand) — only
    // re-measure on real width changes, except while a first successful
    // measure is still pending (a hidden panel becoming visible nudges a
    // synthetic resize with the same width — see image-with-tabs.js).
    const width = window.innerWidth;
    if (width === this.lastWidth && this.measured) return;
    this.lastWidth = width;

    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      if (this.expanded) {
        if (this.content.style.maxHeight !== 'none') {
          this.content.style.maxHeight = `${this.content.scrollHeight}px`;
        }
      } else {
        this.applyCollapsedHeight();
      }
    }, 120);
  }

  applyCollapsedHeight() {
    // Skip measurement when the element is not laid out (display:none,
    // hidden attribute, etc.) — getBoundingClientRect returns zeros and
    // we'd lock the content to max-height: 0. A later resize / activate
    // event will retrigger this once the element is visible.
    if (this.offsetParent === null) return;
    this.measured = true;

    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const wordCount = isMobile ? this.wordCountMobile : this.wordCountDesktop;
    const offset = this.findWordOffset(wordCount);
    if (offset === null) {
      this.content.style.maxHeight = '';
      this.classList.add('expandable-text--full');
      this.button.hidden = true;
      return;
    }
    this.classList.remove('expandable-text--full');
    this.button.hidden = false;
    this.content.style.maxHeight = `${offset}px`;
  }

  findWordOffset(targetWord) {
    if (!targetWord || targetWord < 1) return null;
    const range = document.createRange();
    const walker = document.createTreeWalker(this.content, NodeFilter.SHOW_TEXT);
    const containerTop = this.content.getBoundingClientRect().top;
    let seen = 0;
    let node;

    while ((node = walker.nextNode())) {
      const text = node.textContent || '';
      const matches = text.match(/\S+/g) || [];
      if (seen + matches.length < targetWord) {
        seen += matches.length;
        continue;
      }
      // Cut-off word is inside this node — find char index after the (target - seen)th word.
      const wordsToTake = targetWord - seen;
      let charOffset = text.length;
      let foundWords = 0;
      let inWord = false;
      for (let i = 0; i < text.length; i++) {
        if (/\S/.test(text[i])) {
          if (!inWord) {
            foundWords++;
            inWord = true;
          }
        } else if (inWord) {
          inWord = false;
          if (foundWords >= wordsToTake) {
            charOffset = i;
            break;
          }
        }
      }
      try {
        // Span the last character of the cut-off word instead of placing a
        // collapsed caret — Safari/WebKit returns an all-zero rect for a
        // collapsed range, which turned the clamp height into
        // scroll-position garbage on iOS.
        range.setStart(node, Math.max(0, charOffset - 1));
        range.setEnd(node, charOffset);
        const rect = range.getBoundingClientRect();
        if (rect.top === 0 && rect.bottom === 0 && rect.height === 0) return null;
        return rect.bottom - containerTop;
      } catch (_) {
        return null;
      }
    }
    return null;
  }

  toggle() {
    this.expanded = !this.expanded;
    if (this.expanded) {
      this.content.style.maxHeight = `${this.content.scrollHeight}px`;
      this.classList.add('expandable-text--open');
      this.button.textContent = this.collapseLabel;
      this.button.setAttribute('aria-expanded', 'true');
    } else {
      // The open state may have released max-height to 'none' — re-fix it
      // to a px value and flush layout so the close transition has a
      // concrete start height to animate from.
      this.content.style.maxHeight = `${this.content.scrollHeight}px`;
      this.content.offsetHeight;
      this.applyCollapsedHeight();
      this.classList.remove('expandable-text--open');
      this.button.textContent = this.expandLabel;
      this.button.setAttribute('aria-expanded', 'false');
    }
  }
}

if (!customElements.get('expandable-text')) {
  customElements.define('expandable-text', ExpandableText);
}
